<template>
  <div>
    <!-- Hero Section -->
    <section class="relative overflow-hidden min-h-screen flex items-center pt-16">
      <div class="absolute inset-0 bg-white dark:bg-dark transition-colors duration-300"></div>

      <div class="container mx-auto px-4 relative z-10">
        <div class="max-w-5xl mx-auto text-center">
          <p class="text-[13px] uppercase tracking-[0.2em] text-blue-500 font-medium mb-6">
            {{ $t(`${pageKey}.hero.tagline`) }}
          </p>

          <h1 class="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1] mb-6">
            <span class="text-gray-900 dark:text-white transition-colors duration-300">{{ $t(`${pageKey}.hero.title`) }}</span>
          </h1>

          <p class="text-lg md:text-xl text-gray-500 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed mb-10 transition-colors duration-300">
            {{ $t(`${pageKey}.hero.description`) }}
          </p>

          <div class="flex flex-wrap justify-center gap-4 mb-10">
            <router-link to="/contact" class="inline-flex items-center h-14 px-8 bg-gray-900 dark:bg-white text-white dark:text-dark rounded-xl font-bold text-lg transition-colors duration-300">
              <Globe class="mr-2 h-5 w-5" />
              {{ $t(`${pageKey}.hero.cta`) }}
            </router-link>
          </div>
        </div>
      </div>
      <div class="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white dark:from-dark to-transparent z-10"></div>
    </section>

    <PartnersSection />

    <!-- What You Need to Know Section -->
    <section class="py-28 bg-white dark:bg-dark transition-colors duration-300">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6 transition-colors duration-300">
            {{ $t(`${pageKey}.whatYouNeed.title`) }}
          </h2>
          <p class="text-lg text-gray-500 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed transition-colors duration-300">
            {{ $t(`${pageKey}.whatYouNeed.subtitle`) }}
          </p>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div class="space-y-8">
            <div>
              <h3 class="text-2xl font-bold mb-4 text-gray-900 dark:text-white transition-colors duration-300">{{ $t(`${pageKey}.whatYouNeed.basics.title`) }}</h3>
              <div class="space-y-4">
                <div class="group rounded-2xl p-5 bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] hover:border-blue-200 dark:hover:border-blue-500/20 transition-all duration-300">
                  <h4 class="font-semibold text-gray-900 dark:text-white mb-2 transition-colors duration-300">{{ $t(`${pageKey}.whatYouNeed.basics.what.title`) }}</h4>
                  <p class="text-gray-500 dark:text-gray-400 text-sm leading-relaxed transition-colors duration-300">{{ $t(`${pageKey}.whatYouNeed.basics.what.description`) }}</p>
                </div>
                <div class="group rounded-2xl p-5 bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] hover:border-blue-200 dark:hover:border-blue-500/20 transition-all duration-300">
                  <h4 class="font-semibold text-gray-900 dark:text-white mb-2 transition-colors duration-300">{{ $t(`${pageKey}.whatYouNeed.basics.how.title`) }}</h4>
                  <p class="text-gray-500 dark:text-gray-400 text-sm leading-relaxed transition-colors duration-300">{{ $t(`${pageKey}.whatYouNeed.basics.how.description`) }}</p>
                </div>
                <div class="group rounded-2xl p-5 bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] hover:border-blue-200 dark:hover:border-blue-500/20 transition-all duration-300">
                  <h4 class="font-semibold text-gray-900 dark:text-white mb-2 transition-colors duration-300">{{ $t(`${pageKey}.whatYouNeed.basics.when.title`) }}</h4>
                  <p class="text-gray-500 dark:text-gray-400 text-sm leading-relaxed transition-colors duration-300">{{ $t(`${pageKey}.whatYouNeed.basics.when.description`) }}</p>
                </div>
              </div>
            </div>
          </div>

          <div class="space-y-8">
            <div>
              <h3 class="text-2xl font-bold mb-4 text-gray-900 dark:text-white transition-colors duration-300">{{ $t(`${pageKey}.whatYouNeed.business.title`) }}</h3>
              <div class="space-y-4">
                <div class="group rounded-2xl p-5 bg-blue-50/70 dark:bg-blue-500/[0.08] border border-blue-200/70 dark:border-blue-500/20 transition-all duration-300">
                  <h4 class="font-semibold text-blue-700 dark:text-blue-300 mb-2 transition-colors duration-300">{{ $t(`${pageKey}.whatYouNeed.business.immediate.title`) }}</h4>
                  <p class="text-gray-500 dark:text-gray-400 text-sm leading-relaxed transition-colors duration-300">{{ $t(`${pageKey}.whatYouNeed.business.immediate.description`) }}</p>
                </div>
                <div class="group rounded-2xl p-5 bg-blue-50/70 dark:bg-blue-500/[0.08] border border-blue-200/70 dark:border-blue-500/20 transition-all duration-300">
                  <h4 class="font-semibold text-blue-700 dark:text-blue-300 mb-2 transition-colors duration-300">{{ $t(`${pageKey}.whatYouNeed.business.longterm.title`) }}</h4>
                  <p class="text-gray-500 dark:text-gray-400 text-sm leading-relaxed transition-colors duration-300">{{ $t(`${pageKey}.whatYouNeed.business.longterm.description`) }}</p>
                </div>
                <div class="group rounded-2xl p-5 bg-blue-50/70 dark:bg-blue-500/[0.08] border border-blue-200/70 dark:border-blue-500/20 transition-all duration-300">
                  <h4 class="font-semibold text-blue-700 dark:text-blue-300 mb-2 transition-colors duration-300">{{ $t(`${pageKey}.whatYouNeed.business.roi.title`) }}</h4>
                  <p class="text-gray-500 dark:text-gray-400 text-sm leading-relaxed transition-colors duration-300">{{ $t(`${pageKey}.whatYouNeed.business.roi.description`) }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Benefits Section -->
    <section class="py-28 bg-gray-50 dark:bg-dark transition-colors duration-300">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6 transition-colors duration-300">
            {{ $t(`${pageKey}.benefits.title`) }}
          </h2>
          <p class="text-lg text-gray-500 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed transition-colors duration-300">
            {{ $t(`${pageKey}.benefits.subtitle`) }}
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div v-for="(benefit, index) in benefits" :key="index" class="group rounded-2xl p-6 bg-white dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] hover:border-blue-200 dark:hover:border-blue-500/20 hover:shadow-lg hover:shadow-blue-500/5 transition-all duration-300">
            <div class="w-12 h-12 bg-gray-100 dark:bg-white/[0.06] rounded-lg flex items-center justify-center mb-5 border border-gray-200 dark:border-white/[0.06]">
              <component :is="benefit.icon" class="h-6 w-6 text-gray-500 dark:text-gray-400 group-hover:text-blue-500 transition-colors duration-300" />
            </div>
            <h3 class="text-xl font-semibold mb-3 text-gray-900 dark:text-white transition-colors duration-300">{{ benefit.title }}</h3>
            <p class="text-gray-500 dark:text-gray-400 leading-relaxed transition-colors duration-300">{{ benefit.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- How It Works Section -->
    <section class="py-28 bg-white dark:bg-dark transition-colors duration-300">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6 transition-colors duration-300">
            {{ $t(`${pageKey}.howItWorks.title`) }}
          </h2>
          <p class="text-lg text-gray-500 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed transition-colors duration-300">
            {{ $t(`${pageKey}.howItWorks.subtitle`) }}
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div v-for="(step, index) in howItWorksSteps" :key="index" class="text-center rounded-2xl p-6 bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] transition-colors duration-300">
            <div class="w-16 h-16 bg-gray-100 dark:bg-white/[0.06] rounded-full flex items-center justify-center mx-auto mb-4 border border-gray-200 dark:border-white/[0.06]">
              <component :is="step.icon" class="h-7 w-7 text-blue-500" />
            </div>
            <h3 class="text-lg font-semibold mb-2 text-gray-900 dark:text-white transition-colors duration-300">{{ step.title }}</h3>
            <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed transition-colors duration-300">{{ step.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="relative py-28 md:py-36 overflow-hidden">
      <div class="absolute inset-0 bg-gray-950">
        <img src="/ciobai_hero_background.webp" alt="" class="absolute inset-0 w-full h-full object-cover opacity-60" />
      </div>

      <div class="container mx-auto px-4 text-center relative z-10">
        <h2 class="text-3xl md:text-4xl font-bold text-white mb-6">
          {{ $t(`${pageKey}.cta.title`) }}
        </h2>
        <p class="text-xl text-white/85 mb-10 max-w-3xl mx-auto">
          {{ $t(`${pageKey}.cta.description`) }}
        </p>
        <div class="flex flex-wrap justify-center gap-4">
          <router-link to="/contact" class="inline-flex items-center h-14 px-8 bg-white text-gray-900 rounded-xl font-bold text-lg transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-blue-500/10">
            <Globe class="mr-2 h-5 w-5" />
            {{ $t(`${pageKey}.cta.button`) }}
          </router-link>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { Globe, TrendingUp, Zap, Shield, Check, Users, Target, BarChart3 } from 'lucide-vue-next'
import PartnersSection from './PartnersSection.vue'

const props = defineProps({
  pageKey: {
    type: String,
    required: true
  }
})

const { t } = useI18n()

const benefits = computed(() => [
  {
    icon: TrendingUp,
    title: t(`${props.pageKey}.benefits.benefit1.title`),
    description: t(`${props.pageKey}.benefits.benefit1.description`)
  },
  {
    icon: Zap,
    title: t(`${props.pageKey}.benefits.benefit2.title`),
    description: t(`${props.pageKey}.benefits.benefit2.description`)
  },
  {
    icon: Shield,
    title: t(`${props.pageKey}.benefits.benefit3.title`),
    description: t(`${props.pageKey}.benefits.benefit3.description`)
  },
  {
    icon: Users,
    title: t(`${props.pageKey}.benefits.benefit4.title`),
    description: t(`${props.pageKey}.benefits.benefit4.description`)
  },
  {
    icon: Target,
    title: t(`${props.pageKey}.benefits.benefit5.title`),
    description: t(`${props.pageKey}.benefits.benefit5.description`)
  },
  {
    icon: BarChart3,
    title: t(`${props.pageKey}.benefits.benefit6.title`),
    description: t(`${props.pageKey}.benefits.benefit6.description`)
  }
])

const howItWorksSteps = computed(() => [
  {
    icon: Check,
    title: t(`${props.pageKey}.howItWorks.step1.title`),
    description: t(`${props.pageKey}.howItWorks.step1.description`)
  },
  {
    icon: Zap,
    title: t(`${props.pageKey}.howItWorks.step2.title`),
    description: t(`${props.pageKey}.howItWorks.step2.description`)
  },
  {
    icon: Target,
    title: t(`${props.pageKey}.howItWorks.step3.title`),
    description: t(`${props.pageKey}.howItWorks.step3.description`)
  },
  {
    icon: Shield,
    title: t(`${props.pageKey}.howItWorks.step4.title`),
    description: t(`${props.pageKey}.howItWorks.step4.description`)
  }
])
</script>

